import { computed } from 'vue';
import type { Requirement } from '@/stores/calendar-create-appointment/requirements';
import { useCalendarPreviewStore } from '@/stores/calendar-preview';
import { usePageLayoutStore } from '@/stores/page-layout';
import { useResourcesStore } from '@/stores/resources';
import type {
  AllocationAttributes,
  AppointmentPartAttributes,
  Resource,
  Service,
} from '@/types';

export const getPartDuration = (part: AppointmentPartAttributes): number => {
  if (part.duration) {
    return part.duration;
  } else if (
    typeof part.durationFinish === 'number' &&
    typeof part.durationProcessing === 'number' &&
    typeof part.durationSetup === 'number'
  ) {
    return part.durationFinish + part.durationProcessing + part.durationSetup;
  } else {
    return 0;
  }
};

// Copies a service's durations onto a part. Based on requiresProcessingTime, the durations
// that are not being used are reset to 0.
export const setPartDurations = (
  part: AppointmentPartAttributes,
  service: Service,
) => {
  if (service.requiresProcessingTime) {
    part.durationFinish = service.durationFinish || 0;
    part.durationProcessing = service.durationProcessing || 0;
    part.durationSetup = service.durationSetup || 0;
    part.duration = 0;
  } else {
    part.durationFinish = 0;
    part.durationProcessing = 0;
    part.durationSetup = 0;
    part.duration = (service.duration || 0) + (service.buffer || 0);
  }
};

export const getPartResources = (
  part: AppointmentPartAttributes,
  resourceType: 'employee' | 'room' | 'equipment',
): number[] => {
  const { resourceById } = useResourcesStore();
  const resourceIds =
    part.allocationsAttributes
      ?.reduce((acc, allocation) => acc.concat(allocation.resourceId), [])
      .filter((resourceId) => !!resourceId) || [];
  return resourceType
    ? resourceIds.filter((id) => resourceById(id)?.type === resourceType)
    : resourceIds;
};

type AllocationOptionsInput = {
  allocation: AllocationAttributes;
  // All allocations of the part the allocation belongs to
  allAllocations: AllocationAttributes[];
  requirement: Requirement | null | undefined;
  // The resources of the appointment's location
  resources: Resource[];
  availableResourceIds: number[] | null | undefined;
};

// The resources an allocation's dropdown lists: those of the requirement's type,
// except the ones already picked for another requirement of the same part
export const getAllocationResources = ({
  allocation,
  allAllocations,
  requirement,
  resources,
}: Omit<AllocationOptionsInput, 'availableResourceIds'>): Resource[] => {
  const resourceType = requirement?.type.toLowerCase() || 'employee';
  const takenResourceIds =
    allAllocations.length === 1
      ? []
      : allAllocations
          .filter(
            (other) =>
              other.resourceId &&
              other.requirementId &&
              other.requirementId !== allocation.requirementId,
          )
          .map((other) => other.resourceId);

  return resources.filter(
    (resource) =>
      resource.type === resourceType && !takenResourceIds.includes(resource.id),
  );
};

// Of those, the resources that meet the requirement and are available
export const getValidAllocationResourceIds = (
  input: AllocationOptionsInput,
): Resource['id'][] =>
  getAllocationResources(input)
    .filter(
      (resource) =>
        (!input.requirement ||
          input.requirement.resourceIds.includes(resource.id)) &&
        (!input.availableResourceIds ||
          input.availableResourceIds.includes(resource.id)),
    )
    .map((resource) => resource.id);

// Whether an allocation can be left on "Any" (an empty resource). Never for the first allocation,
// and only while there is more than one valid resource to pick from.
export const canAllocationBeAny = (
  allocationIndex: number | undefined,
  validResourceIds: Resource['id'][],
): boolean => allocationIndex !== 0 && validResourceIds.length > 1;

export const isSmallScreen = computed(() => {
  const { screenSize } = usePageLayoutStore();
  const { previewModeActive } = useCalendarPreviewStore();

  return screenSize === 's' || (screenSize === 'm' && previewModeActive);
});
